/**
 * useLoginForm — Canonical login-form state + handlers hook.
 *
 * Takes the app's auth context values as parameters (login / verifyMfa /
 * cancelMfa / clearError / isLoading / error / mfaRequired / mfaMethods /
 * preferredMethod) rather than consuming a specific AuthContext, so the hook
 * is app-agnostic.
 *
 * The client-side sign-in throttle is ON BY DEFAULT (Q-UMB-20, owner ruling 2026-09-24: "safe
 * default + stated opt-out"): `rateLimitConfig` defaults to `LOGIN_RATE_LIMIT_CONFIG`, a surface
 * that must differ passes its own policy, and ONLY an explicit `null` turns it off — every such
 * opt-out is pinned, with its written reason, by `loginThrottleOptOuts.contract.test.js`. The
 * engine is `@puffin-core/form-security`'s `useRateLimit`, counting FAILURES only.
 *
 * Optional config objects (not passing them preserves the base behaviour):
 *   - csrfConfig:      provides per-submit CSRF header map
 *   - subdomainConfig: adds subdomain state, detection source, and change handler
 *   - credentialPersistenceConfig: persists email (+ subdomain) on remember-me
 *   - redirectConfig:  overrides onSuccess with cross-domain redirect behavior
 *
 * @module @puffin-core/login-shell/hooks/useLoginForm
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import { RELOAD_BLOCK_REASON } from '@puffin-core/api-client';
import { useReloadBlocker } from '@puffin-core/api-client/react';
import { getOwnWebPublicUrl } from '@puffin-core/cross-app-policy';
import {
  useRateLimit,
  resolveRateLimitPolicy,
  LOGIN_RATE_LIMIT_CONFIG,
} from '@puffin-core/form-security/core';

import { DEFAULT_CREDENTIAL_REFUSAL_CODES, describeSignInRefusal } from '../utils/signInRefusal.js';
import { MFA_PASSKEY_METHOD, answerWithPasskey, mfaRefusalOf } from '../utils/mfaPasskeyAnswer.js';

/** Shared empty map: a fresh `{}` default would rebuild the submit handler every render. */
const NO_REFUSAL_MESSAGES = Object.freeze({});

/**
 * W14: whether a value breaks its descriptor's own shape (`minLength`, `pattern` — a RegExp source).
 *
 * @param {string} value
 * @param {?{minLength?: number, pattern?: string}} rule
 * @returns {boolean}
 */
function breaksRule(value, rule) {
  if (!rule) return false;
  if (typeof rule.minLength === 'number' && value.length < rule.minLength) return true;
  return typeof rule.pattern === 'string' && !new RegExp(rule.pattern).test(value);
}

/**
 * W14: the refusal for the first credential that breaks its descriptor — the descriptor's own
 * (translated) sentence, else the catalog's — or null when both hold.
 *
 * @param {?{identifier?: Object, secret?: Object}} rules
 * @param {?string} identifier
 * @param {string} secret
 * @returns {?{message: ?string, messageKey: ?string, locked: boolean}}
 */
function shapeRefusal(rules, identifier, secret) {
  const broken = [
    [identifier, rules?.identifier],
    [secret, rules?.secret],
  ].find(([value, rule]) => value !== null && breaksRule(value, rule));
  if (!broken) return null;
  const sentence = broken[1].invalidMessage;
  return sentence
    ? { message: sentence, messageKey: null, locked: false }
    : { message: null, messageKey: 'login.error.credentialsInvalid', locked: false };
}

/**
 * Resolve the destination for the "Not your company? Sign in elsewhere"
 * affordance: the app's TENANT-NEUTRAL apex login (the company picker), NOT
 * the current tenant subdomain (Login-1). `getOwnWebPublicUrl` derives the
 * environment-correct apex (localhost / dev-prefix / prod) from the current
 * `window.location`, so a click on `dtownretail.plannerpuffin.com` lands on
 * `https://plannerpuffin.com/login`, and a localhost dev session stays on
 * `http://localhost:<port>/login`.
 *
 * When `appKey` is absent (a LoginShell mount not yet wired), fall back to the
 * current-host root. A foreign host needs no fallback: `getOwnWebPublicUrl`
 * resolves it to the app's prod apex. An UNKNOWN `appKey` is a wiring defect
 * and surfaces (`getOwnWebPublicUrl` throws a `TypeError`); it used to be
 * caught and answered `'/'` (SWALLOWED-CATCH-BURNDOWN-001 U3). Every shipped
 * `appKey` is a registry key (census of the login mounts).
 *
 * Exported for unit testing.
 *
 * @param {string} appKey - Registry app key (e.g. `'planner'`).
 * @returns {string} The absolute URL (or `'/'` when no `appKey` is wired).
 * @throws {TypeError} when `appKey` is not a registry key.
 */
export function resolveSignInElsewhereUrl(appKey) {
  if (typeof appKey !== 'string' || appKey.length === 0) return '/';
  const origin = getOwnWebPublicUrl(appKey, {
    sourceScheme: window.location.protocol.replace(/:$/, ''),
    sourceHostname: window.location.host,
  });
  return `${origin}/login`;
}

/**
 * @typedef {Object} AuthState
 * @property {(email:string, password:string, captchaToken:string|null, extras?: Object) => Promise<any>} login
 * @property {(code:string, method?:string) => Promise<any>} [verifyMfa]
 * @property {() => void} [cancelMfa]
 * @property {() => void} [clearError]
 * @property {boolean} [isLoading]
 * @property {string|null} [error]
 * @property {boolean} [mfaRequired]
 * @property {string[]} [mfaMethods]
 * @property {string} [preferredMethod]
 * @property {string} [maskedPhone]
 * @property {string} [maskedEmail]
 * @property {(method:string)=>Promise<any>} [onSendOtp]
 */

/**
 * @typedef {Object} RateLimitConfig
 * @property {number} maxAttempts   Failed attempts inside the window that trigger the lockout.
 * @property {number} windowMs      Sliding window, in ms, the failures are counted in.
 * @property {number} [lockoutMs]   Lockout duration in ms; defaults to `windowMs`.
 */

/**
 * @typedef {Object} CsrfConfig
 * @property {() => Object} getHeaders  Returns CSRF headers to merge into submit.
 */

/**
 * @typedef {Object} SubdomainConfig
 * @property {string} [value]   CONTROLLED subdomain value (parent-owned; from URL,
 *   storage, or user input). When provided, the parent is the single source of
 *   truth — `useLoginForm` derives the submitted subdomain from this on EVERY
 *   render, so a value the parent updates (e.g. a page-owned `<SubdomainPrompt>`
 *   slot) can never drift from what the form submits (AUTH-LOGIN-FIDELITY-001).
 *   When absent, the form falls back to its own internal state (seeded from a
 *   persisted remember-me subdomain) — the uncontrolled/tenant path.
 * @property {'url' | 'storage' | 'input'} [source='input']  How it was populated.
 * @property {(subdomain: string) => void} [onChange]  External change subscriber.
 *   In the controlled flow the parent updates its own state here; the new value
 *   flows back via `value`.
 */

/**
 * @typedef {Object} CredentialPersistenceConfig
 * @property {() => {email?:string, subdomain?:string, remember?:boolean}} [load]
 * @property {(values: {email?:string, subdomain?:string}) => void} save
 * @property {() => void} [clear]
 */

/**
 * @typedef {Object} RedirectConfig
 * @property {(result:any, context:{subdomain?:string}) => string} buildUrl
 * @property {'in-domain' | 'cross-domain'} [mode='in-domain']
 * @property {(subdomain: string) => string} [buildTenantLoginUrl] - cross-domain only (N152): the company's own sign-in
 *   page, where a held sign-in (403 `MFA_SETUP_REQUIRED`) enrols
 */

/**
 * @typedef {'login.attempt'|'login.success'|'login.failure'
 *   |'mfa.required'|'mfa.success'|'mfa.failure'
 *   |'mfa.enrollment_required'|'mfa.enrollment_completed'
 *   |'rate_limit.triggered'
 *   |'captcha.verified'|'captcha.expired'} AuthEventType
 */

/**
 * @typedef {Object} AuthEvent
 * @property {AuthEventType} type
 * @property {Object} payload   Event-specific payload (see `fireAuthEvent`).
 */

/**
 * Callback signature for `onAuthEvent`. Fires at eleven security-relevant
 * choke points through the login/MFA/captcha/rate-limit flow. Payload
 * NEVER contains tokens, passwords, or raw backend error messages — safe
 * for consumer-side analytics + audit log forwarding.
 *
 * Event shapes:
 *   - `login.attempt`         { email, subdomain?, rememberMe }
 *   - `login.success`         { email, subdomain? }
 *   - `login.failure`         { email, subdomain?, code?, errorClass? }
 *   - `mfa.required`          { email, subdomain?, methods, preferredMethod }
 *   - `mfa.success`           { method }
 *   - `mfa.failure`           { method?, code?, errorClass? }
 *   - `mfa.enrollment_required`  { email, subdomain? }  // platform: no second factor yet
 *   - `mfa.enrollment_completed` { email, subdomain? }  // backup codes acknowledged, signed in
 *   - `rate_limit.triggered`  { email, lockedUntil, lockoutMs }
 *   - `captcha.verified`      { tokenLength }  // NEVER leaks the token string
 *   - `captcha.expired`       {}
 *
 * A handler that throws is caught internally and logged to `console.warn`;
 * the login flow continues unaffected.
 *
 * @typedef {(type: AuthEventType, payload: Object) => void} AuthEventHandler
 */

/* ------------------------------------------------------------------------- *
 * Module-private state slices.
 *
 * `useLoginForm` is a 38-hook orchestrator. These are NOT a flat lift of those
 * hooks into one wrapper (which would just move the problem) — each slice owns
 * one concern and is composed by the thin domain hook below. None are exported
 * from index.js; the public surface is `useLoginForm` alone.
 * ------------------------------------------------------------------------- */

/**
 * The credentials the user types, plus the remember-me preference that decides
 * whether they're persisted. Every change handler clears a stale auth error, so
 * they all need `auth`.
 *
 * LOGIN-CONVERGENCE W4 + W7: the slice also holds the form's own word on the LAST attempt with these
 * credentials (`refusal`) — its blank-field refusal, or the server's refusal as `describeSignInRefusal`
 * worded it; `null` when nothing names it, which leaves the auth context's own `error` to be shown.
 * It belongs to the credentials because editing them is what makes it stale: every change clears it.
 */
function useLoginCredentials({ auth, persisted, lockedIdentifier }) {
  const [typedEmail, setEmail] = useState(persisted?.email ?? '');
  // W18: a LOCKED identity (a step-up proves the signed-in person, never another) is the identifier,
  // whatever the field's state says.
  const email = lockedIdentifier ?? typedEmail;
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(Boolean(persisted?.remember));
  const [refusal, setRefusal] = useState(null);
  // W16: the MFA step's word on the last refused code — `{ messageKey, terminal }` — held here
  // because a refused verify RESOLVES (the shell never lets it escape as an unhandled rejection).
  const [mfaRefusal, setMfaRefusal] = useState(null);

  const clearRefusal = useCallback(() => setRefusal(null), []);

  const handleEmailChange = useCallback(
    (e) => {
      setEmail(e.target.value);
      clearRefusal();
      if (auth.error && auth.clearError) auth.clearError();
    },
    [auth, clearRefusal],
  );

  const handlePasswordChange = useCallback(
    (e) => {
      setPassword(e.target.value);
      clearRefusal();
      if (auth.error && auth.clearError) auth.clearError();
    },
    [auth, clearRefusal],
  );

  const togglePassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleRememberMeChange = useCallback((e) => {
    setRememberMe(e.target.checked);
  }, []);

  // WEB-APP-VERSION-REFRESH-001: a typed password holds back the reload a new build asks for, so a sign-in screen
  // left open on a register never erases it under the person typing. Not the email: a remembered address is
  // pre-filled, and would hold the reload back forever.
  useReloadBlocker(password.length > 0, RELOAD_BLOCK_REASON.SIGN_IN_IN_PROGRESS);

  return {
    email,
    password,
    showPassword,
    rememberMe,
    refusal,
    setRefusal,
    mfaRefusal,
    setMfaRefusal,
    handleEmailChange,
    handlePasswordChange,
    togglePassword,
    handleRememberMeChange,
  };
}

/**
 * The two bot mitigations the login form carries: the decoy and the CAPTCHA token lifecycle.
 *
 * LOGIN-CONVERGENCE W1 (2026-10-11): the decoy is page-shell's `HoneypotField` (SEC-HONEYPOT-001),
 * read through its ref's `isFilled()`. The form used to render its own `<input name="website"
 * autoComplete="off">` — the exact shape that standard records as wrong, because a password manager
 * fills an `<input>` and ignores `off` on a field with a common name, and on a SIGN-IN form a password
 * manager is always present. A filled decoy ended the submit silently, so such a person saw a sign-in
 * that did nothing.
 *
 * `resetCaptcha` is the single place that both resets the widget and drops the token — the submit
 * path calls it on failure so a spent token is never re-submitted (fail-closed).
 */
function useLoginBotDefenses({ fireAuthEvent }) {
  const honeypotRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const isHoneypotFilled = useCallback(
    () => Boolean(honeypotRef.current && honeypotRef.current.isFilled()),
    [],
  );

  const handleCaptchaVerify = useCallback(
    (token) => {
      setCaptchaToken(token);
      // Fire tokenLength-only payload — never leak the raw CAPTCHA token
      // to analytics (it's a short-lived server challenge response but
      // could still be misused if intercepted).
      fireAuthEvent('captcha.verified', {
        tokenLength: typeof token === 'string' ? token.length : 0,
      });
    },
    [fireAuthEvent],
  );

  const handleCaptchaExpire = useCallback(() => {
    setCaptchaToken(null);
    fireAuthEvent('captcha.expired', {});
  }, [fireAuthEvent]);

  const handleCaptchaError = useCallback(() => {
    setCaptchaToken(null);
  }, []);

  const resetCaptcha = useCallback(() => {
    if (captchaRef.current) {
      captchaRef.current.reset();
      setCaptchaToken(null);
    }
  }, []);

  return {
    honeypotRef,
    isHoneypotFilled,
    captchaToken,
    captchaRef,
    handleCaptchaVerify,
    handleCaptchaExpire,
    handleCaptchaError,
    resetCaptcha,
  };
}

/**
 * The client-side sign-in throttle: `@puffin-core/form-security`'s ONE engine, counting FAILURES
 * inside a sliding window, forgetting them on success, and locking for `lockoutMs`.
 *
 * 🔴 This slice used to BE an engine — a module-private `useLoginRateLimit` with its own attempt
 * ledger, lockout interval and countdown, the second client throttle in the fleet beside
 * form-security's. Q-UMB-20 (2026-09-24) promoted its semantics into form-security and deleted it,
 * so a sign-in form and a throttled auth form now run the same code.
 *
 * The hook is ALWAYS called (hooks cannot be conditional); an explicit `null` opt-out only stops
 * this slice from gating or recording, and hands `null` back so no notice renders.
 *
 * @param {Object} params
 * @param {RateLimitConfig|null} params.rateLimitConfig - the resolved prop: a policy, or
 *   `null` for a stated opt-out
 * @param {AuthEventHandler} params.fireAuthEvent
 * @returns {{rateLimit: Object|null, recordFailure: (email: string) => void,
 *   recordSuccess: () => void}}
 */
function useLoginThrottle({ rateLimitConfig, fireAuthEvent }) {
  const isOptedOut = rateLimitConfig === null;
  const policy = isOptedOut ? LOGIN_RATE_LIMIT_CONFIG : rateLimitConfig;
  const { lockoutMs } = useMemo(() => resolveRateLimitPolicy(policy), [policy]);
  const engine = useRateLimit(policy);
  const { recordFailure: engineRecordFailure, recordSuccess: engineRecordSuccess } = engine;

  const recordFailure = useCallback(
    (contextEmail) => {
      if (isOptedOut) return;
      const lockedUntil = engineRecordFailure();
      if (lockedUntil !== null) {
        fireAuthEvent('rate_limit.triggered', { email: contextEmail, lockedUntil, lockoutMs });
      }
    },
    [isOptedOut, engineRecordFailure, fireAuthEvent, lockoutMs],
  );

  const recordSuccess = useCallback(() => {
    if (isOptedOut) return;
    engineRecordSuccess();
  }, [isOptedOut, engineRecordSuccess]);

  // LOGIN-CONVERGENCE W3: the SERVER's throttle refused, naming its lockout. The ONE client throttle
  // locks for exactly that long, so `<RateLimitNotice>` shows one refusal and one countdown whichever
  // side refused — and the event reads the same as the client's own lock.
  const { lockFor: engineLockFor } = engine;
  const lockFor = useCallback(
    (durationMs, contextEmail) => {
      if (isOptedOut) return;
      engineLockFor(durationMs);
      fireAuthEvent('rate_limit.triggered', {
        email: contextEmail,
        lockedUntil: Date.now() + durationMs,
        lockoutMs: durationMs,
      });
    },
    [isOptedOut, engineLockFor, fireAuthEvent],
  );

  return { rateLimit: isOptedOut ? null : engine, recordFailure, recordSuccess, lockFor };
}

/**
 * AUTH-LOGIN-FIDELITY-001: when `subdomainConfig.value` is provided the field is
 * CONTROLLED — the parent owns the value and `subdomain` is derived from it on
 * every render, so the value the form submits (`extras.subdomain`) can never
 * drift from what the parent renders (the bug: a once-seeded internal copy that
 * ignored later parent updates, so a typed subdomain was never submitted).
 * Internal state remains only for the UNCONTROLLED path (tenant/persisted).
 */
function useLoginSubdomain({ subdomainConfig, persisted, auth }) {
  const isSubdomainControlled = subdomainConfig?.value !== undefined;
  const [internalSubdomain, setInternalSubdomain] = useState(
    subdomainConfig?.value ?? persisted?.subdomain ?? '',
  );
  const subdomain = isSubdomainControlled ? subdomainConfig.value : internalSubdomain;
  const isSubdomainFromUrl = subdomainConfig?.source === 'url';

  const setSubdomain = useCallback(
    (value) => {
      // Always notify the parent. In the CONTROLLED flow the parent owns the
      // value and the new value flows back via `subdomainConfig.value`, so we do
      // NOT also write internal state (that would create the dual source of truth
      // this fix removes). In the UNCONTROLLED flow we own the value here.
      if (subdomainConfig?.onChange) subdomainConfig.onChange(value);
      if (subdomainConfig?.value === undefined) {
        setInternalSubdomain(value);
      }
    },
    [subdomainConfig],
  );

  const handleSubdomainChange = useCallback(
    (e) => {
      setSubdomain(e.target.value);
      if (auth.error && auth.clearError) auth.clearError();
    },
    [setSubdomain, auth],
  );

  return { subdomain, setSubdomain, isSubdomainFromUrl, handleSubdomainChange };
}

/**
 * Open/close state for the password-reset and contact-manager modals, and whether a reset has just
 * finished (W6).
 *
 * `passwordResetInitiallyOpen` opens the reset on arrival — the storefront's "you already have an
 * account" email links to its sign-in with `?reset=1`, and the page that reads that is the one that
 * knows it. A finished reset is remembered so the form can say "sign in with it now", and the
 * surface hears it through `onPasswordResetSuccess`.
 */
function useLoginModals({ passwordResetInitiallyOpen, onPasswordResetSuccess }) {
  const [passwordResetOpen, setPasswordResetOpen] = useState(passwordResetInitiallyOpen);
  const [passwordResetDone, setPasswordResetDone] = useState(false);
  const [contactManagerOpen, setContactManagerOpen] = useState(false);

  const handlePasswordResetSuccess = useCallback(() => {
    setPasswordResetDone(true);
    if (onPasswordResetSuccess) onPasswordResetSuccess();
  }, [onPasswordResetSuccess]);

  const openPasswordReset = useCallback(() => {
    setPasswordResetOpen(true);
  }, []);

  const closePasswordReset = useCallback(() => {
    setPasswordResetOpen(false);
  }, []);

  const openContactManager = useCallback(() => {
    setContactManagerOpen(true);
  }, []);

  const closeContactManager = useCallback(() => {
    setContactManagerOpen(false);
  }, []);

  return {
    passwordResetOpen,
    passwordResetDone,
    handlePasswordResetSuccess,
    contactManagerOpen,
    openPasswordReset,
    closePasswordReset,
    openContactManager,
    closeContactManager,
  };
}

/**
 * The submit paths: password login, MFA verify/cancel, remember-me persistence,
 * and the tenant-neutral "sign in elsewhere" escape. Reads the slices above; owns
 * no state of its own.
 */
function useLoginSubmit({
  auth,
  onSuccess,
  onAuthenticatorChallenge,
  csrfConfig,
  subdomainConfig,
  credentialPersistenceConfig,
  redirectConfig,
  appKey,
  fireAuthEvent,
  email,
  password,
  rememberMe,
  isHoneypotFilled,
  captchaToken,
  resetCaptcha,
  isThrottled,
  recordFailure,
  recordSuccess,
  lockFor,
  setRefusal,
  setMfaRefusal,
  hasIdentifier,
  refusalMessages,
  credentialRefusalCodes,
  credentialsRequiredMessage,
  captchaRequired,
  credentialRules,
  subdomain,
}) {
  // RME-001: persist (or clear) the remember-me email. Called from EVERY success
  // path — handleSubmit (no-MFA), handleMfaVerify (MFA) and handleMfaEnrollmentFinish
  // (platform enrolment, Q-L16-98) — because handleSubmit returns early on both. Keeping this in one place stops the two
  // paths from drifting (the original bug: only handleSubmit saved, so MFA users
  // never had their email remembered).
  const persistRememberMe = useCallback(() => {
    if (!credentialPersistenceConfig?.save) return;
    if (rememberMe) {
      credentialPersistenceConfig.save({ email, subdomain: subdomain || undefined });
    } else if (credentialPersistenceConfig.clear) {
      credentialPersistenceConfig.clear();
    }
  }, [credentialPersistenceConfig, rememberMe, email, subdomain]);

  // W3 + W7: a refused attempt is read ONCE — what it does to the throttle and what the form says.
  const handleRefused = useCallback(
    (err, identifier) => {
      const read = describeSignInRefusal(err, { refusalMessages, credentialRefusalCodes });
      if (read.lockoutMs !== null) lockFor(read.lockoutMs, identifier);
      else if (read.spendsAttempt) recordFailure(identifier);
      setRefusal(
        read.message || read.messageKey
          ? { message: read.message, messageKey: read.messageKey, locked: read.locked }
          : null,
      );
    },
    [refusalMessages, credentialRefusalCodes, lockFor, recordFailure, setRefusal],
  );

  const handleSubmit = useCallback(
    async (e) => {
      if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
      }
      // W1: a filled decoy means a bot — end silently (no request, no signal to the sender), and
      // tell the surface, which may audit it.
      if (isHoneypotFilled()) {
        fireAuthEvent('honeypot.tripped', {});
        return;
      }
      if (isThrottled) return;
      // W4 (FVA-001): the form's own check owns the submit gate and FAILS CLOSED — a blank credential
      // is never sent, never counted and never reported. Only the identifier is trimmed: trimming a
      // secret would silently mangle a valid one.
      const identifier = hasIdentifier ? email.trim() : null;
      if ((hasIdentifier && !identifier) || !password) {
        setRefusal(
          credentialsRequiredMessage
            ? { message: credentialsRequiredMessage, messageKey: null, locked: false }
            : { message: null, messageKey: 'login.error.credentialsRequired', locked: false },
        );
        return;
      }
      // W14: a credential that breaks its descriptor's own shape is refused by the form — nothing sent,
      // nothing counted — with the descriptor's sentence.
      const malformed = shapeRefusal(credentialRules, identifier, password);
      if (malformed) {
        setRefusal(malformed);
        return;
      }
      // W10 (CAPTCHA-POLICY-001): a required check with no token is refused HERE too, not only by the
      // disabled button — a keyboard or programmatic submit must never send a token-less request.
      if (captchaRequired && !captchaToken) {
        setRefusal({ message: null, messageKey: 'login.error.captchaRequired', locked: false });
        return;
      }
      setRefusal(null);
      const subdomainContext = subdomainConfig ? subdomain || undefined : undefined;
      fireAuthEvent('login.attempt', {
        email: identifier,
        subdomain: subdomainContext,
        rememberMe,
      });
      try {
        const extras = {};
        if (csrfConfig?.getHeaders) extras.headers = csrfConfig.getHeaders();
        if (subdomainConfig) extras.subdomain = subdomain;
        const result = await auth.login(identifier, password, captchaToken, extras);
        // 🔴 BEFORE the MFA branch, and deliberately so. A user with BOTH factors gets a response
        // carrying `requiresMfa` AND `requiresAuthenticator`, with the server's own
        // `preferredMethod: 'push'` — because a challenge has already been created and the phone
        // is buzzing as this renders. Showing the TOTP entry field first would ask the user to
        // ignore what their device is already asking them.
        //
        // The MFA payload is passed through intact, so the shell's "Use code instead" fallback
        // still has the `mfaToken` it needs.
        if (result?.requiresAuthenticator) {
          fireAuthEvent('authenticator.required', {
            email: identifier,
            subdomain: subdomainContext,
            challengeId: result.challengeId || null,
          });
          if (onAuthenticatorChallenge) onAuthenticatorChallenge(result);
          return;
        }
        // Q-L16-98 · F-98-16 — the platform gate refused a staff member with no second factor and
        // handed over an enrolment token. Not a success: the shell swaps to the enrolment screen
        // from `auth.mfaEnrollmentRequired`, and the throttle is not reset (no credential proof
        // completed a sign-in).
        // N152: the marketing apex's twin — the held person is handed to their company's sign-in page.
        if (result?.requiresMfaEnrollment || result?.requiresMfaEnrollmentHandoff) {
          fireAuthEvent('mfa.enrollment_required', {
            email: identifier,
            subdomain: subdomainContext,
          });
          return;
        }
        if (result?.requiresMfa) {
          fireAuthEvent('mfa.required', {
            email: identifier,
            subdomain: subdomainContext,
            // Every adapter answers its methods (login convergence, 2026-10-19).
            methods: result.mfaMethods,
            preferredMethod: result.preferredMethod || null,
          });
          return;
        }
        recordSuccess();
        fireAuthEvent('login.success', { email: identifier, subdomain: subdomainContext });
        persistRememberMe();
        if (redirectConfig?.buildUrl) {
          // MKT-LOGIN-XDOMAIN-001/HANDOFF: buildUrl may be async (cross-domain
          // mode performs a server handoff to mint a one-time tenant-session
          // token before redirecting). Await covers sync + async resolvers.
          const url = await redirectConfig.buildUrl(result, { subdomain });
          window.location.href = url;
          return;
        }
        if (onSuccess) onSuccess(result);
      } catch (err) {
        handleRefused(err, identifier);
        fireAuthEvent('login.failure', {
          email: identifier,
          subdomain: subdomainContext,
          code: err?.code ?? err?.status ?? null,
          errorClass: err?.errorClass || null,
        });
        resetCaptcha();
      }
    },
    [
      email,
      password,
      hasIdentifier,
      credentialsRequiredMessage,
      captchaRequired,
      credentialRules,
      isHoneypotFilled,
      captchaToken,
      auth,
      onSuccess,
      onAuthenticatorChallenge,
      isThrottled,
      csrfConfig,
      subdomainConfig,
      subdomain,
      redirectConfig,
      rememberMe,
      recordSuccess,
      fireAuthEvent,
      persistRememberMe,
      resetCaptcha,
      handleRefused,
      setRefusal,
    ],
  );

  // W16 (LOGIN-CONVERGENCE): a refused code RESOLVES. `<MfaVerification>` calls `onVerify` without
  // awaiting it, so a rethrow here was an unhandled rejection on every refused code in the fleet. The
  // refusal is HANDLED instead: reported (`mfa.failure`) and held for the MFA screen as its catalog key
  // and terminal flag (`mfaRefusal`); a refusal with no key leaves the auth context's own `error`.
  // ONE tail for every answer — a code, or a passkey (Q-EE-34 P5) — so the two cannot drift.
  const answerMfaChallenge = useCallback(
    async (verify, method) => {
      setMfaRefusal(null);
      try {
        const result = await verify();
        fireAuthEvent('mfa.success', { method: method || null });
        // RME-001: handleSubmit returned early on the MFA challenge, so this is
        // the ONLY save point for MFA users — persist the remember-me email here.
        persistRememberMe();
        // MKT-LOGIN-XDOMAIN-001/HANDOFF: the MFA path must redirect the SAME way
        // as the password path. Previously only handleSubmit honored
        // redirectConfig, so a cross-domain MFA login completed but never reached
        // the tenant. Mirror the password path: redirectConfig wins (async-safe);
        // tenant mode (no redirectConfig) falls through to onSuccess/AuthContext.
        if (redirectConfig?.buildUrl) {
          const url = await redirectConfig.buildUrl(result, { subdomain });
          window.location.href = url;
          return result;
        }
        if (onSuccess) onSuccess(result);
        return result;
      } catch (err) {
        fireAuthEvent('mfa.failure', {
          method: method || null,
          code: err?.code ?? err?.status ?? null,
          errorClass: err?.errorClass || null,
        });
        setMfaRefusal(mfaRefusalOf(err));
        return undefined;
      }
    },
    [onSuccess, fireAuthEvent, persistRememberMe, redirectConfig, subdomain, setMfaRefusal],
  );

  const handleMfaVerify = useCallback(
    (code, method) =>
      auth.verifyMfa ? answerMfaChallenge(() => auth.verifyMfa(code, method), method) : undefined,
    [auth, answerMfaChallenge],
  );

  // Q-EE-34 P5: offered by `<MfaVerification>` only when the surface can serve a passkey (see the return).
  const handleMfaPasskeyVerify = useCallback(
    () => answerMfaChallenge(() => answerWithPasskey(auth), MFA_PASSKEY_METHOD),
    [auth, answerMfaChallenge],
  );

  const handleMfaCancel = useCallback(() => {
    setMfaRefusal(null);
    if (auth.cancelMfa) auth.cancelMfa();
  }, [auth, setMfaRefusal]);

  // Q-L16-98 · F-98-16 — the staff member acknowledged the backup codes: the enrolment leg
  // publishes the session it held, and the sign-in completes on the SAME tail as the MFA path
  // (remember-me, then redirectConfig or onSuccess), because an enrolment IS a completed sign-in.
  const handleMfaEnrollmentFinish = useCallback(async () => {
    const user = auth.finishMfaEnrollment();
    fireAuthEvent('mfa.enrollment_completed', {
      email,
      subdomain: subdomainConfig ? subdomain || undefined : undefined,
    });
    persistRememberMe();
    if (redirectConfig?.buildUrl) {
      window.location.href = await redirectConfig.buildUrl(user, { subdomain });
      return;
    }
    if (onSuccess) onSuccess(user);
  }, [
    auth,
    email,
    subdomainConfig,
    onSuccess,
    fireAuthEvent,
    persistRememberMe,
    redirectConfig,
    subdomain,
  ]);

  const handleNotYourCompany = useCallback(() => {
    if (credentialPersistenceConfig?.clear) credentialPersistenceConfig.clear();
    // Login-1: land on the tenant-NEUTRAL apex login, not the current tenant
    // subdomain (which would re-resolve the same company the user is leaving).
    window.location.href = resolveSignInElsewhereUrl(appKey);
  }, [credentialPersistenceConfig, appKey]);

  return {
    handleSubmit,
    handleMfaVerify,
    handleMfaPasskeyVerify: auth.verifyMfaWithPasskey ? handleMfaPasskeyVerify : undefined,
    handleMfaCancel,
    handleMfaEnrollmentFinish,
    handleNotYourCompany,
  };
}

/**
 * @param {Object} params
 * @param {AuthState} params.auth
 * @param {() => void} [params.onSuccess]
 * @param {RateLimitConfig|null} [params.rateLimitConfig=LOGIN_RATE_LIMIT_CONFIG] The sign-in
 *   throttle policy. ON BY DEFAULT; pass a policy to differ, or `null` — with a reason recorded in
 *   `loginThrottleOptOuts.contract.test.js` — to turn it off.
 * @param {CsrfConfig} [params.csrfConfig]
 * @param {SubdomainConfig} [params.subdomainConfig]
 * @param {CredentialPersistenceConfig} [params.credentialPersistenceConfig]
 * @param {RedirectConfig} [params.redirectConfig]
 * @param {AuthEventHandler} [params.onAuthEvent]
 */
export function useLoginForm({
  auth,
  onSuccess,
  // AUTHENTICATOR-CHALLENGE-FANOUT (2026-08-29): called with the login result when the server
  // answers `requiresAuthenticator` — a push-2FA challenge has been created and delivered to the
  // user's PalmPuffin device. The shell swaps the form for `<AuthenticatorChallengeFlow>`.
  onAuthenticatorChallenge,
  rateLimitConfig = LOGIN_RATE_LIMIT_CONFIG,
  csrfConfig,
  subdomainConfig,
  credentialPersistenceConfig,
  redirectConfig,
  onAuthEvent,
  appKey,
  // LOGIN-CONVERGENCE (2026-10-11) — the generic seams, each documented in the README:
  // W8: a sign-in with no identifier (a single access code) submits the secret alone.
  hasIdentifier = true,
  // W4: the surface's own (translated) sentence for a blank form; the catalog's otherwise.
  credentialsRequiredMessage = null,
  // W7: the surface's own words for its server's refusal codes (`describeSignInRefusal`).
  refusalMessages = NO_REFUSAL_MESSAGES,
  // W3: the codes that prove a wrong secret, and so spend an attempt.
  credentialRefusalCodes = DEFAULT_CREDENTIAL_REFUSAL_CODES,
  // W6: open the reset on arrival; hear a finished reset.
  passwordResetInitiallyOpen = false,
  onPasswordResetSuccess,
  // W10: whether the CAPTCHA policy requires a token (`<LoginForm>` passes its ONE derivation).
  captchaRequired = false,
  // W14: the credential descriptors' own shapes (`{ identifier, secret }`, each `{ minLength, pattern,
  // invalidMessage }`); `<LoginForm>` passes its `credentialFields`.
  credentialRules = null,
  // W18: an identifier the sign-in is LOCKED to (a step-up re-authenticates the signed-in person).
  lockedIdentifier = null,
} = {}) {
  if (!auth || typeof auth.login !== 'function') {
    throw new Error(
      'useLoginForm requires an `auth` param with at least a `login(email, password, captchaToken)` function.',
    );
  }

  // Credential persistence: load initial values from storage (if configured).
  const persisted = credentialPersistenceConfig?.load ? credentialPersistenceConfig.load() : null;

  const [selectedAuthMethod, setSelectedAuthMethod] = useState('email_password');

  // Item 5: observability hook. Created FIRST: every slice below fires through it. Not wrapped
  // (SWALLOWED-CATCH-BURNDOWN-001 U3): `onAuthEvent` is the HOST's code, and a throwing handler is a
  // defect in it — the old catch made it vanish with no trace anywhere.
  const fireAuthEvent = useCallback(
    (type, payload) => {
      if (typeof onAuthEvent !== 'function') return;
      onAuthEvent(type, payload);
    },
    [onAuthEvent],
  );

  const credentials = useLoginCredentials({ auth, persisted, lockedIdentifier });
  const botDefenses = useLoginBotDefenses({ fireAuthEvent });
  const throttle = useLoginThrottle({ rateLimitConfig, fireAuthEvent });
  const subdomainState = useLoginSubdomain({ subdomainConfig, persisted, auth });
  const modals = useLoginModals({ passwordResetInitiallyOpen, onPasswordResetSuccess });

  const submit = useLoginSubmit({
    auth,
    onSuccess,
    onAuthenticatorChallenge,
    csrfConfig,
    subdomainConfig,
    credentialPersistenceConfig,
    redirectConfig,
    appKey,
    fireAuthEvent,
    email: credentials.email,
    password: credentials.password,
    rememberMe: credentials.rememberMe,
    isHoneypotFilled: botDefenses.isHoneypotFilled,
    captchaToken: botDefenses.captchaToken,
    resetCaptcha: botDefenses.resetCaptcha,
    isThrottled: Boolean(throttle.rateLimit?.isLimited),
    recordFailure: throttle.recordFailure,
    recordSuccess: throttle.recordSuccess,
    lockFor: throttle.lockFor,
    setRefusal: credentials.setRefusal,
    setMfaRefusal: credentials.setMfaRefusal,
    hasIdentifier,
    refusalMessages,
    credentialRefusalCodes,
    credentialsRequiredMessage,
    captchaRequired,
    credentialRules,
    subdomain: subdomainState.subdomain,
  });

  return {
    // Auth state (pass-through from injected auth)
    isLoading: Boolean(auth.isLoading),
    error: auth.error ?? null,
    mfaRequired: Boolean(auth.mfaRequired),
    mfaEnrollmentRequired: Boolean(auth.mfaEnrollmentRequired),
    mfaEnrollmentHandoff: auth.mfaEnrollmentHandoff ?? null,
    mfaMethods: auth.mfaMethods ?? [],
    preferredMethod: auth.preferredMethod,
    maskedPhone: auth.maskedPhone,
    maskedEmail: auth.maskedEmail,

    // Form state
    email: credentials.email,
    password: credentials.password,
    showPassword: credentials.showPassword,
    honeypotRef: botDefenses.honeypotRef,
    rememberMe: credentials.rememberMe,
    captchaToken: botDefenses.captchaToken,
    captchaRef: botDefenses.captchaRef,
    selectedAuthMethod,
    setSelectedAuthMethod,
    passwordResetOpen: modals.passwordResetOpen,
    passwordResetDone: modals.passwordResetDone,
    contactManagerOpen: modals.contactManagerOpen,

    // W4 + W7: the form's own word on the last attempt, or `null` (see `useLoginCredentials`).
    refusal: credentials.refusal,
    // W16: the MFA step's word on the last refused code, or `null`.
    mfaRefusal: credentials.mfaRefusal,

    // Subdomain state (populated only when subdomainConfig is passed)
    subdomain: subdomainState.subdomain,
    setSubdomain: subdomainState.setSubdomain,
    isSubdomainFromUrl: subdomainState.isSubdomainFromUrl,
    handleSubdomainChange: subdomainState.handleSubdomainChange,

    // The sign-in throttle's `useRateLimit` state — what `<RateLimitNotice>` renders — or `null`
    // under a stated opt-out.
    rateLimit: throttle.rateLimit,

    // Handlers
    handleSubmit: submit.handleSubmit,
    handleMfaVerify: submit.handleMfaVerify,
    handleMfaPasskeyVerify: submit.handleMfaPasskeyVerify,
    handleMfaCancel: submit.handleMfaCancel,
    handleMfaEnrollmentFinish: submit.handleMfaEnrollmentFinish,
    handleEmailChange: credentials.handleEmailChange,
    handlePasswordChange: credentials.handlePasswordChange,
    togglePassword: credentials.togglePassword,
    handleRememberMeChange: credentials.handleRememberMeChange,
    handleCaptchaVerify: botDefenses.handleCaptchaVerify,
    handleCaptchaExpire: botDefenses.handleCaptchaExpire,
    handleCaptchaError: botDefenses.handleCaptchaError,
    openPasswordReset: modals.openPasswordReset,
    closePasswordReset: modals.closePasswordReset,
    handlePasswordResetSuccess: modals.handlePasswordResetSuccess,
    openContactManager: modals.openContactManager,
    closeContactManager: modals.closeContactManager,
    handleNotYourCompany: submit.handleNotYourCompany,
  };
}

export default useLoginForm;
